import { useCallback, useState } from 'react';
import { toChatErrorCode } from '../client/errorCode';
import { buildImageUploadMessage } from '../upload';
import { useChatMessages } from './useChatMessages';
import type { ChatApi } from '../client/client';
import type { ChatErrorCode } from '../client/types';
import type { PendingGenerativeUiComponent } from '../components/generative-ui';
import type { ChatImageAttachment, DisplayMessage, ReportError } from '../messages';
import type { StagedAttachment } from '../upload';

/**
 * The POST does not stream — it returns one JSON body after the whole LLM round trip,
 * so a send legitimately takes seconds. This bounds "slow" so a hung request becomes a
 * retryable error instead of a spinner that never resolves.
 */
const SEND_TIMEOUT_MS = 90_000;

/** Consecutive failures after which retrying stops being worth offering. */
const MAX_RETRIES = 2;

/**
 * Failures that will never succeed on retry because the conversation itself is closed.
 * Distinct from `invalid_request`, which is also terminal but indicates a client bug
 * rather than a legitimate end state.
 */
const BLOCKING_ERROR_CODES = ['conversation_full', 'brief_not_draft'] as const;

export type ChatBlockedReason = (typeof BLOCKING_ERROR_CODES)[number];

/**
 * Why the last send failed.
 *
 * A nullable object rather than a boolean plus a code, because `code` is legitimately
 * null for a network failure or an HTML error page — so "failed with no code" and "did
 * not fail" need to stay distinguishable without a second flag to keep in step.
 */
type ChatFailure = { kind: 'stale' } | { kind: 'error'; code: ChatErrorCode | null };

/**
 * `error` is recoverable by retrying, `blocked` by nothing, `stale` only by reloading.
 * Three states because there are three distinct recoveries — collapsing `stale` into
 * `error` would offer a retry that is guaranteed to fail with the same code.
 */
export type ChatSendState = 'idle' | 'sending' | 'error' | 'blocked' | 'stale';

export type UseChatResult = {
    /** Loaded history plus every locally added message, in thread order. */
    messages: DisplayMessage[];
    loading: boolean;
    loadError: Error | null;
    sendState: ChatSendState;
    /** Consecutive send failures. */
    retryCount: number;
    /**
     * Whether the retry budget is spent. Exposed as a decision rather than a count so
     * MAX_RETRIES stays in one place — consumers previously each redefined it and
     * compared for themselves.
     */
    retriesExhausted: boolean;
    /** Set when the conversation is closed for good; no retry is offered. */
    blockedReason: ChatBlockedReason | null;
    /** The contract error code behind an `error` state, for choosing copy. */
    errorCode: ChatErrorCode | null;
    /**
     * The *load* failure's contract discriminator — distinct from `errorCode` above,
     * which is send-specific. Passed straight through from `useChatHistory` (via
     * `useChatMessages`) since a consumer has no other route to it: calling
     * `useChatHistory` a second time would fetch the session twice.
     */
    loadErrorCode: ChatErrorCode | null;
    /** Re-runs the initial history load. `useChatHistory`'s real reload, passed straight through — never a send retry; see `onRetry` for that. */
    reload: () => void;
    /** True once the bounded reload budget above is spent. */
    reloadBudgetExhausted: boolean;
    /**
     * The one reply that should reveal word by word: the last message hydration found
     * when it loaded (only if that was the assistant's), or whichever reply the most
     * recent send settled since — never both, and never more than one.
     */
    latestReplyKey: string | null;
    inputText: string;
    onInputChange: (value: string) => void;
    /** Files the customer has uploaded and staged, not yet sent. */
    attachments: StagedAttachment[];
    /** Appends newly uploaded files to the staged set. */
    onAttachmentsAdd: (added: StagedAttachment[]) => void;
    /** Removes a staged file by its upload id, before send. */
    onAttachmentRemove: (uploadId: string) => void;
    onSend: () => void;
    onRetry: () => void;
    /** The follow-up question from the most recent turn, for the composer to render. */
    pendingComponent: PendingGenerativeUiComponent | null;
    /** Sends a generative-UI reply's wire-content string as the next message. */
    onSubmitComponentReply: (content: string) => void;
    /** Whether the brief is complete, per the backend's `completedAt` signal. */
    isBriefCompleted: boolean;
    isMessageLimitReached: boolean;
};

/**
 * Owns the send lifecycle: dispatching a message, timing it out, and classifying how it
 * failed.
 *
 * The thread itself belongs to `useChatMessages` — this hook never touches message
 * ordering, keys or counts. What is left here is the part that is genuinely about
 * sending: one in-flight flag, one failure, and the retry budget.
 *
 * Divergence between this client and the service is only ever detected by the service
 * rejecting a send with `conversation_stale`, and the only recovery is a reload.
 */
export function useChat(briefId: string, api: ChatApi, onError?: ReportError): UseChatResult {
    const thread = useChatMessages(briefId, api, onError);

    const [inputText, setInputText] = useState('');
    const [attachments, setAttachments] = useState<StagedAttachment[]>([]);
    const [isSending, setIsSending] = useState(false);
    const [failure, setFailure] = useState<ChatFailure | null>(null);
    const [retryCount, setRetryCount] = useState(0);

    const { addPending, addPendingComponentReply, settleSent, settleFailed, retryPending } = thread;

    const runSend = useCallback(
        async (key: string, message: string, expectedMessageCount: number) => {
            setIsSending(true);

            const controller = new AbortController();
            // AbortSignal.timeout would be neater but is absent from jsdom, and
            // polyfilling it in tests would mean testing a different code path than
            // ships. An explicit controller behaves identically everywhere.
            const timeout = setTimeout(() => controller.abort(), SEND_TIMEOUT_MS);

            try {
                const { data, error } = await api.POST('/briefs/{brief_id}/chat-session/message', {
                    params: { path: { brief_id: briefId } },
                    body: { message, expectedMessageCount },
                    signal: controller.signal,
                });

                if (error !== undefined || data === undefined) {
                    fail(key, toChatErrorCode(error));
                    return;
                }

                const component = data.message.component ?? data.component;
                settleSent(
                    key,
                    data.message.content,
                    data.messageCount,
                    component,
                    data.completedAt
                );
                setFailure(null);
                setRetryCount(0);
            } catch (cause) {
                fail(key, toChatErrorCode(cause));
            } finally {
                clearTimeout(timeout);
                setIsSending(false);
            }

            function fail(failedKey: string, code: ChatErrorCode | null) {
                // The bubble stays on screen, matching the pilot: the customer needs to
                // see what failed to decide whether to edit it or retry it.
                settleFailed(failedKey);

                if (code === 'conversation_stale') {
                    // Not an error: retrying cannot help, because our count is wrong and
                    // we cannot learn the right one without refetching. Only a reload
                    // recovers, so this does not consume the retry budget either.
                    setFailure({ kind: 'stale' });
                    return;
                }

                setFailure({ kind: 'error', code });
                setRetryCount((count) => count + 1);
            }
        },
        [api, briefId, settleSent, settleFailed]
    );

    const errorCode = failure?.kind === 'error' ? failure.code : null;
    const blockedReason = isBlockingErrorCode(errorCode) ? errorCode : null;

    const sendState: ChatSendState = isSending
        ? 'sending'
        : failure === null
        ? 'idle'
        : failure.kind === 'stale'
        ? 'stale'
        : blockedReason !== null
        ? 'blocked'
        : 'error';

    const retriesExhausted = retryCount >= MAX_RETRIES;

    const isMessageLimitReached =
        (thread.maxMessages !== null && thread.serverMessageCount >= thread.maxMessages) ||
        blockedReason === 'conversation_full';

    const canSend =
        !thread.loading &&
        thread.loadError === null &&
        sendState !== 'sending' &&
        sendState !== 'stale' &&
        blockedReason === null &&
        !retriesExhausted;

    const onSend = useCallback(() => {
        const caption = inputText.trim();
        const hasAttachments = attachments.length > 0;

        if ((caption === '' && !hasAttachments) || !canSend) return;

        setInputText('');

        if (hasAttachments) {
            // `content` is the JSON envelope actually sent (and re-sent on retry); `image`
            // is the render-only form so the optimistic bubble shows thumbnails.
            const wireMessage = buildImageUploadMessage(attachments, caption);
            const image = {
                attachments: attachments.map(toChatImageAttachment),
                caption: caption === '' ? undefined : caption,
            };
            setAttachments([]);
            void runSend(addPending(wireMessage, image), wireMessage, thread.serverMessageCount);
            return;
        }

        void runSend(addPending(caption), caption, thread.serverMessageCount);
    }, [addPending, attachments, canSend, inputText, runSend, thread.serverMessageCount]);

    const onAttachmentsAdd = useCallback((added: StagedAttachment[]) => {
        setAttachments((previous) => [...previous, ...added]);
    }, []);

    const onAttachmentRemove = useCallback((uploadId: string) => {
        setAttachments((previous) => previous.filter((att) => att.uploadId !== uploadId));
    }, []);

    const onRetry = useCallback(() => {
        const failed = thread.lastFailed;
        if (failed === null || !canSend) return;

        retryPending(failed.key);
        // The count is unchanged: a failed send persists nothing server-side, which every
        // error path in the service guarantees.
        void runSend(failed.key, failed.content, thread.serverMessageCount);
    }, [canSend, retryPending, runSend, thread.lastFailed, thread.serverMessageCount]);

    const onSubmitComponentReply = useCallback(
        (content: string) => {
            if (!canSend) return;
            void runSend(addPendingComponentReply(content), content, thread.serverMessageCount);
        },
        [addPendingComponentReply, canSend, runSend, thread.serverMessageCount]
    );

    return {
        messages: thread.messages,
        loading: thread.loading,
        loadError: thread.loadError,
        sendState,
        retryCount,
        retriesExhausted,
        blockedReason,
        errorCode,
        loadErrorCode: thread.errorCode,
        reload: thread.reload,
        reloadBudgetExhausted: thread.reloadBudgetExhausted,
        latestReplyKey: thread.latestReplyKey,
        inputText,
        onInputChange: setInputText,
        attachments,
        onAttachmentsAdd,
        onAttachmentRemove,
        onSend,
        onRetry,
        pendingComponent: thread.pendingComponent,
        onSubmitComponentReply,
        isBriefCompleted: thread.completedAt !== undefined,
        isMessageLimitReached,
    };
}

/** The render-only projection of a staged attachment: drops the send-time base64 payload. */
function toChatImageAttachment(att: StagedAttachment): ChatImageAttachment {
    return {
        uploadId: att.uploadId,
        previewUrl: att.previewUrl,
        fileName: att.fileName,
        mediaType: att.mediaType,
        fileSize: att.fileSize != null ? Number(att.fileSize) : undefined,
    };
}

function isBlockingErrorCode(code: ChatErrorCode | null): code is ChatBlockedReason {
    return code !== null && (BLOCKING_ERROR_CODES as readonly string[]).includes(code);
}
